import { useQBR } from "./use-qbr";
import styles from "./QBRTable.module.css";
import { useEffect, useMemo } from "react";
import { EditorContent } from "@tiptap/react";
import { injectTeamColumns } from "./qbr-table-html";
import { useQBREditor } from "./use-qbr-editor";
import { TeamCellCallbacks } from "./QBRTableCellExtension";
import "pages/TopicViewer/OutputStyle.css";

export const TeamQBR = () => {
  const { qbrData, getRowColor, fetchQBRData } = useQBR();

  const owners = useMemo(
    () =>
      Object.values(qbrData?.qbr_scores || {}).filter((owner) => owner.active),
    [qbrData?.qbr_scores]
  );
  const showCalculatedColumns = owners.length > 1;

  const getScoreValue = (
    scoresList: Record<string, { score: string }>,
    rowIndex: number
  ) => {
    const raw = scoresList?.[rowIndex]?.score;
    if (raw == "") return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getRowStats = (rowIndex: number) => {
    const values = owners
      .map((owner) => getScoreValue(owner.scores_list, rowIndex))
      .filter((val): val is number => typeof val === "number");
    if (values.length === 0) return { avg: 0, variance: 0 };
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance =
      values.reduce((sum, val) => sum + (val - avg) ** 2, 0) / values.length;
    return { avg, variance };
  };

  const getVarianceColor = (value: number) => {
    if (value <= 0.25) return styles.varianceGreen;
    if (value <= 0.5) return styles.varianceYellow;
    if (value <= 0.75) return styles.varianceOrange;
    return styles.varianceRed;
  };

  const getScoreBadgeClass = (value?: string | number) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return "";
    const needsDarkText = parsed >= 1.875 && parsed < 2.5;
    return needsDarkText ? styles.badgeTextDark : "";
  };

  const getScoreBadgeColor = (value?: string | number) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return "";
    return getRowColor(parsed.toString());
  };

  const getOwnerAverage = (scoresList?: Record<string, { score: string }>) => {
    const values = Object.values(scoresList || {})
      .map((item) => Number(item?.score))
      .filter((val) => Number.isFinite(val));
    if (values.length === 0) return 0;
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  };

  const rowCount = useMemo(() => {
    if (!qbrData?.text) return 0;
    const parser = new DOMParser();
    const doc = parser.parseFromString(qbrData.text, "text/html");
    return doc.querySelectorAll("tbody tr").length;
  }, [qbrData?.text]);

  const getObjectiveAvgAverage = () => {
    if (rowCount === 0) return 0;
    const values = Array.from(
      { length: rowCount },
      (_, i) => getRowStats(i).avg
    );
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  };

  const getObjectiveVarAverage = () => {
    if (rowCount === 0) return 0;
    const values = Array.from(
      { length: rowCount },
      (_, i) => getRowStats(i).variance
    );
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  };

  const teamCallbacks = useMemo<TeamCellCallbacks>(
    () => ({
      owners,
      getRowColor,
      getScoreValue,
      getScoreBadgeClass,
      getScoreBadgeColor,
      getVarianceColor,
      getRowStats,
      getOwnerAverage,
      getObjectiveAvgAverage,
      getObjectiveVarAverage
    }),
    [owners, getRowColor, rowCount]
  );

  const tableHtml = useMemo(() => {
    if (!qbrData?.text) return null;
    return injectTeamColumns(qbrData.text, {
      owners,
      showCalculatedColumns
    });
  }, [qbrData?.text, owners, showCalculatedColumns]);

  const editor = useQBREditor({
    html: tableHtml,
    teamCallbacks
  });

  // Re-render all QBR NodeView cells when data changes so rationale bubbles stay fresh
  useEffect(() => {
    if (!editor) return;
    try {
      editor.view.dom
        .querySelectorAll("td")
        .forEach((td: any) => td.__qbrRender?.());
    } catch {
      // Editor view not yet mounted
    }
  }, [editor, qbrData]);

  useEffect(() => {
    fetchQBRData();
  }, []);

  if (!tableHtml) return null;

  return (
    <div className={`document-style ${styles.qbrWrapper}`}>
      <EditorContent editor={editor} className={styles.qbrEditorContent} />
    </div>
  );
};
